/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
	SVGIcon,
	Label
} from '@ba/ba-ui-carbon-toolkit';

import favorite32 from '@ba/ba-graphics/dist/icons-js/favorite_32';

import menuOverflow16 from '@ba/ba-graphics/dist/icons-js/overflow-menu--vertical_32';
import checkMarkIcon from '@ba/ba-graphics/dist/icons-js/checkmark--filled_24';
import ContextMenuIcon from 'components/contextMenu/ContextMenuIcon';

import './Card.scss';

const FEATURES = {
	FAVOURITE: false
};

class Card extends Component {

	constructor(props) {
		super(props);

		this.el = null;
	}

	componentDidMount() {
		this.props.cacheNodeRef && this.props.cacheNodeRef(this.props.id, this.el);
	}

	componentWillUnmount() {
		this.props.cacheNodeRef && this.props.cacheNodeRef(this.props.id, null);
	}

	render() {
		const {
			view,
			title,
			tabIndex,
			onClick,
			isSelected,
			isHidden
		} = this.props;

		const footer = this.renderFooter();

		const classes = classNames({
			card: true,
			selected: isSelected,
			'is-hidden': isHidden
		});

		return (
			<div
				className={classes}
				tabIndex={tabIndex}
				onClick={onClick}
				onContextMenu={(event) => this.onContextMenu(event)}
				ref={node => this.el = node}
			>
				{isSelected && <SVGIcon
					className='checkMarkIcon'
					iconId={checkMarkIcon.id}
					height={14}
					width={14}> </SVGIcon>}
				{!isHidden && title && <Label className={'cardTitleLabel'} label={title} ellipsis/>}
				<div
					className="card-view"
				>
					{view}
				</div>
				{footer}
			</div>
		);
	}

	renderFooter() {
		if (!this.props.hasFooter) {
			return;
		}

		const favouriteIcon = this.renderFavouriteIcon();
		const contextMenu = this.renderContextMenu();

		return <div className="card-footer">
			{favouriteIcon}
			{contextMenu}
		</div>;
	}

	renderContextMenu() {
		const {
			contextMenuItems,
			onContextMenuChange,
			onContextMenuToggle
		} = this.props;

		return <div className="card-footer-contextMenu">
			<ContextMenuIcon
				iconId={menuOverflow16.id}
				items={contextMenuItems}
				onChange={(event) => onContextMenuChange(event, this.props.id)}
				onToggle={() => onContextMenuToggle(!contextMenuItems)}
			/>
		</div>;
	}

	renderFavouriteIcon() {
		if (!FEATURES.FAVOURITE) {
			return <div className="favourite"></div>;
		}

		const {
			isFavourite,
			onToggleFavourite
		} = this.props;

		const classes = classNames({
			favourite: true,
			on: isFavourite,
			off: !isFavourite
		});

		return <div className={classes} onClick={() => onToggleFavourite()}>
			<SVGIcon className="on" iconId={favorite32.id} />
			<SVGIcon className="off" iconId={favorite32.id} />
		</div>;
	}

	onContextMenu(event) {
		const {
			contextMenuItems,
			onContextMenuToggle
		} = this.props;

		onContextMenuToggle && onContextMenuToggle(!contextMenuItems);
		event.preventDefault();
	}

}

Card.propTypes = {
	id: PropTypes.string.isRequired,
	title: PropTypes.string,
	view: PropTypes.element,
	hasFooter: PropTypes.bool,
	onClick: PropTypes.func,
	onContextMenuChange: PropTypes.func,
	onContextMenuToggle: PropTypes.func,
	onToggleFavourite: PropTypes.func,
	isSelected: PropTypes.bool,
	isFavourite: PropTypes.bool,
	isHidden: PropTypes.bool,
	contextMenuItems: PropTypes.array,
	tabIndex: PropTypes.string,
	cacheNodeRef: PropTypes.func
};

Card.defaultProps = {
	hasFooter: true,
	onClick: () => {},
	onToggleFavourite: () => {},
	onContextMenuChange: () => {},
	onContextMenuToggle: () => {},
	isSelected: false,
	isFavourite: false,
	isVisible: true,
	contextMenuItems: null
};

export default Card;



// WEBPACK FOOTER //
// ./src/js/components/cards/Card.js